/* import __COLOCATED_TEMPLATE__ from './language-support-message.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type LanguagesService from 'embercom/services/languages-service';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import {
  Status,
  ANSWER_STATE_ANSWER_TYPE_MAP,
  AnswerState,
} from 'embercom/lib/fin-playground/question';

interface LanguageSupportMessageArgs {
  questionDetectedLocale?: string;
  detectedLocale?: string;
  answerType?: string;
  status?: string;
  questionFallbackSearchLocale?: string;
}

interface ListItem {
  key: string;
  htmlSafe?: boolean;
  params?: Record<string, unknown>;
}

interface MessageContent {
  introKey: string;
  introParams: Record<string, unknown>;
  listItems: ListItem[];
}

/**
 * Component that displays different messages based on language detection and support status.
 * Shows appropriate messages with action links for language support and RTT.
 */
export default class LanguageSupportMessage extends Component<LanguageSupportMessageArgs> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare languagesService: LanguagesService;

  get languageLink() {
    let translatedText = this.intl.t(
      'ai-agent.playground.fin-answer.language-support-message.both-disabled.language-support',
      { language: this.languageName },
    );
    let url = this.router.urlFor('apps.app.settings.workspace.general');
    // Add href with generated URL and target="_blank"
    return sanitizeHtml(
      `<a class="cursor-pointer text-neutral-default font-semibold" href="${url}" target="_blank">${translatedText}</a>`,
    );
  }

  get rttLink() {
    let translatedText = this.intl.t(
      'ai-agent.playground.fin-answer.language-support-message.both-disabled.rtt',
    );
    let url = this.router.urlFor('apps.app.automation.fin-ai-agent.settings', {
      queryParams: {
        section: 'multilingual-settings',
      },
    });
    // Add href with generated URL and target="_blank"
    return sanitizeHtml(
      `<a class="cursor-pointer text-neutral-default font-semibold" href="${url}" target="_blank">${translatedText}</a>`,
    );
  }

  get isRttEnabled() {
    return this.languagesService.multilingualSettings?.fallbackSearchLocaleEnabled === true;
  }

  get isLanguageSettingsLoading() {
    return (
      this.languagesService.isLanguagesLoading ||
      this.languagesService.isMultilingualSettingsLoading
    );
  }

  get shouldShowLanguageSupportBanner(): boolean {
    if (this.args.status === Status.Running) {
      return false;
    }

    if (!this.args.questionDetectedLocale) {
      return false;
    }

    if (this.isLanguageSettingsLoading) {
      return false;
    }

    if (
      this.languagesService.languages?.defaultLocale &&
      this.args.questionDetectedLocale === this.languagesService.languages.defaultLocale
    ) {
      return false;
    }

    return this.messageType !== null;
  }

  get shouldShowLanguageMismatchMessage() {
    return (
      this.args.questionDetectedLocale &&
      this.args.detectedLocale &&
      this.args.questionDetectedLocale !== this.args.detectedLocale
    );
  }

  get shouldShowRttDisabledMessage() {
    return (
      !this.isRttEnabled &&
      ANSWER_STATE_ANSWER_TYPE_MAP[AnswerState.Unanswered].includes(this.args.answerType || '')
    );
  }

  get shouldShowRttEnabledMessage() {
    let questionHasNoFallbackLocale = !this.args.questionFallbackSearchLocale;

    return this.isRttEnabled && questionHasNoFallbackLocale;
  }

  get isLanguageNowSupported() {
    let { questionDetectedLocale = '' } = this.args;

    if (!this.languagesService.languages?.supportedLocales) {
      return false;
    }

    let supportedLanguages = this.languagesService.languages.supportedLocales
      .filter((locale) => locale.isPermitted)
      .map((locale) => locale.localeId);

    if (!Array.isArray(supportedLanguages)) {
      return false;
    }

    return supportedLanguages.includes(questionDetectedLocale);
  }

  get languageName() {
    let { questionDetectedLocale = '' } = this.args;
    return this.languagesService.getLanguageName(questionDetectedLocale);
  }

  get messageType(): 'bothDisabled' | 'rttDisabled' | 'languageDisabled' | null {
    // We only use the question's state at the time it was processed, not current system settings

    // If the question has a fallback search locale, it means RTT was enabled when it was processed
    let questionHasRttEnabled = Boolean(this.args.questionFallbackSearchLocale);

    // If the detected locale of the answer and question match, it means language support was available
    // during processing, otherwise it wasn't
    let questionHadLanguageSupport = this.args.detectedLocale === this.args.questionDetectedLocale;

    // If RTT was disabled during processing AND language support was disabled
    if (!questionHasRttEnabled && !questionHadLanguageSupport) {
      return 'bothDisabled';
    }

    // If RTT was enabled during processing BUT language support was disabled
    if (questionHasRttEnabled && !questionHadLanguageSupport) {
      return 'languageDisabled';
    }

    // If RTT was disabled during processing BUT language support was enabled
    if (!questionHasRttEnabled && questionHadLanguageSupport) {
      return 'rttDisabled';
    }

    return null;
  }

  get messageContent(): MessageContent {
    if (!this.messageType) {
      let baseTranslationPath = 'ai-agent.playground.fin-answer.language-support-message';
      return {
        introKey: `${baseTranslationPath}.default.intro`,
        introParams: { language: this.languageName },
        listItems: [],
      };
    }

    let baseTranslationPath = 'ai-agent.playground.fin-answer.language-support-message';

    let messageMap: Record<string, MessageContent> = {
      bothDisabled: {
        introKey: `${baseTranslationPath}.both-disabled.intro`,
        introParams: { language: this.languageName },
        listItems: [
          {
            key: `${baseTranslationPath}.both-disabled.list-item-1`,
            params: { languageLink: this.languageLink },
            htmlSafe: true,
          },
          {
            key: `${baseTranslationPath}.both-disabled.list-item-2`,
            params: { rttLink: this.rttLink },
            htmlSafe: true,
          },
          {
            key: `${baseTranslationPath}.both-disabled.list-item-3`,
            params: {},
          },
        ],
      },
      rttDisabled: {
        introKey: `${baseTranslationPath}.rtt-disabled.intro`,
        introParams: { language: this.languageName },
        listItems: [
          {
            key: `${baseTranslationPath}.rtt-disabled.list-item-1`,
            params: { rttLink: this.rttLink },
            htmlSafe: true,
          },
          {
            key: `${baseTranslationPath}.rtt-disabled.list-item-2`,
            params: {},
          },
        ],
      },
      languageDisabled: {
        introKey: `${baseTranslationPath}.language-disabled.intro`,
        introParams: { language: this.languageName },
        listItems: [
          {
            key: `${baseTranslationPath}.language-disabled.list-item-1`,
            params: { languageLink: this.languageLink },
            htmlSafe: true,
          },
          {
            key: `${baseTranslationPath}.language-disabled.list-item-2`,
            params: {},
          },
        ],
      },
    };

    return messageMap[this.messageType];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::FinAnswer::LanguageSupportMessage': typeof LanguageSupportMessage;
  }
}
